/**
 * UnifiedChip Component
 *
 * A unified chip/badge component with 5 visual variants for displaying
 * restaurant attributes across the consumer app. Serves as the core
 * visual building block for category badges, keyword tags, toggle states,
 * cuisine labels, and highlight chips.
 *
 * Variants (all use gradient border via CSS mask-composite pseudo-element):
 *   - category:  Purple-to-pink gradient border, transparent bg, card-paired text, icon + label
 *   - highlight: Pink-to-gold gradient border, transparent bg, card-paired text, icon + label
 *   - keyword:   Indigo-to-mint gradient border, transparent bg, card-paired text
 *   - toggle:    Pink-to-purple gradient border, transparent bg, card-paired text, 8px radius
 *   - cuisine:   Blue-to-green gradient border, transparent bg, flag emoji prefix
 *   - vibe:      Per-chip DB gradient border (customGradient), transparent bg, emoji prefix
 *
 * Text/icon color always reads `--card-text` (mode-paired) — every current
 * consumer renders this chip inside a `--card-bg` surface (see RestaurantProfile's
 * `cardSurfaceProps` wrapper Boxes), never directly on the canvas. A hardcoded
 * `whiteAlpha.800` here previously read fine on the dark card in dark mode but
 * went near-invisible against the white `--card-bg` in light mode.
 *
 * Uses framer-motion for hover/tap animations with reduced motion support.
 */

import React, { useCallback } from 'react';
import { Badge, Box, Text } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { getCategoryIconComponent } from '@/utils/categoryIcons';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { useRedeemLearnMoreView } from '@/api/mutations/useLearnMoreMutations';

// =============================================================================
// Motion Components
// =============================================================================

const MotionBadge = motion.create(Badge);

// =============================================================================
// Variant Types
// =============================================================================

type UnifiedChipVariant = 'category' | 'highlight' | 'keyword' | 'toggle' | 'cuisine' | 'vibe';

// =============================================================================
// Gradient Border Configuration
// =============================================================================

/**
 * CSS gradient used for the ::before pseudo-element border on each chip variant.
 * Applied via mask-composite to create a transparent-center gradient stroke effect.
 */
const VARIANT_GRADIENTS: Record<UnifiedChipVariant, string> = {
  category:  'linear-gradient(135deg, #A78BFA, #F472B6)',
  cuisine:   'linear-gradient(135deg, #60A5FA, #34D399)',
  highlight: 'linear-gradient(135deg, #F472B6, #FBBF24)',
  keyword:   'linear-gradient(135deg, #818CF8, #6EE7B7)',
  toggle:    'linear-gradient(135deg, #F472B6, #A78BFA)',
  vibe:      'linear-gradient(135deg, #F472B6, #FB923C)',
};

/**
 * Text color per variant — reads the mode-paired `--card-text` token rather
 * than a literal, since every consumer renders this chip on a `--card-bg`
 * surface. `--card-bg`/`--card-text` flip together (white bg + near-black
 * text in light mode, dark bg + near-white text in dark mode), so this stays
 * correct in both appearance modes instead of only the mode the literal was
 * tuned for.
 */
const VARIANT_TEXT_COLORS: Record<UnifiedChipVariant, string> = {
  category:  'var(--card-text)',
  cuisine:   'var(--card-text)',
  highlight: 'var(--card-text)',
  keyword:   'var(--card-text)',
  toggle:    'var(--card-text)',
  vibe:      'var(--card-text)',
};

/** Font weight per variant */
const VARIANT_FONT_WEIGHTS: Record<UnifiedChipVariant, string> = {
  category:  '600',
  cuisine:   '500',
  highlight: '600',
  keyword:   '500',
  toggle:    '500',
  vibe:      '500',
};

// =============================================================================
// Variant Style Configuration
// =============================================================================

/** Hover scale per variant */
const HOVER_SCALES: Record<UnifiedChipVariant, number> = {
  category: 1.03,
  highlight: 1.05,
  keyword: 1.04,
  toggle: 1.02,
  cuisine: 1.03,
  vibe: 1.04,
};

/** Icon size (px) per variant, proportional to chip size tier */
const ICON_SIZES: Record<UnifiedChipVariant, number> = {
  category: 17,
  cuisine: 15,
  highlight: 15,
  keyword: 14,
  toggle: 14,
  vibe: 15,
};

// =============================================================================
// Props
// =============================================================================

export interface UnifiedChipProps {
  /** Visual variant determining the chip's appearance */
  variant: UnifiedChipVariant;
  /** Display text for the chip */
  label: string;
  /** Optional Lucide icon to display before the label */
  icon?: LucideIcon;
  /** Color override for the icon (Chakra token or CSS color) */
  iconColor?: string;
  /** Keyword group key for group-based coloring (highlight variant) */
  group?: string;
  /** Emoji shown before the label (cuisine flag or vibe icon) */
  flagEmoji?: string;
  /** Accent color for border/glow effects (CSS hex, e.g. cuisine flag color) */
  accentColor?: string;
  /** Numeric amount shown after the label (toggle variant, e.g., 2) */
  amount?: number;
  /** Unit for the amount (toggle variant, e.g., "hours") */
  amountUnit?: string;
  /** Category key for icon/color lookup (category variant) */
  categoryKey?: string;
  /** Optional URL to open when the chip is clicked (cuisine variant) */
  learnMoreUrl?: string;
  /**
   * D43b Phase 4 — server-minted Learn More view token paired with
   * learnMoreUrl. When present, redeemed (fire-and-forget) alongside
   * opening the URL. Never fabricate this client-side.
   */
  learnMoreToken?: string;
  /**
   * The member's Learn More token has not arrived yet (the profile opened from
   * list data and `/summary`, the only source of tokens, is still in flight).
   * Renders the chip inert: opening the URL now would award no points, silently.
   */
  learnMorePending?: boolean;
  /** Custom CSS gradient string overriding the variant's default gradient border */
  customGradient?: string;
}

// =============================================================================
// Style Helpers
// =============================================================================

/**
 * Shared ::before pseudo-element styles for the gradient border effect.
 * Uses CSS mask-composite to create a transparent-center gradient stroke.
 * The pseudo-element inherits `borderRadius` from the parent via `borderRadius: 'inherit'`.
 *
 * @param gradient - CSS linear-gradient string for the border
 */
function gradientBorderSx(gradient: string) {
  return {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '1.5px',
      background: gradient,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
  };
}

/**
 * Build Chakra style props for a given variant.
 * All variants now use a gradient border pseudo-element with transparent background.
 */
function getVariantStyles(variant: UnifiedChipVariant, customGradient?: string) {
  const gradient = customGradient ?? VARIANT_GRADIENTS[variant];
  const textColor = VARIANT_TEXT_COLORS[variant];
  const fontWeight = VARIANT_FONT_WEIGHTS[variant];

  // Size presets per variant (mdl → smd → sm)
  const sizeTier = variant === 'category' ? 'mdl'
    : (variant === 'cuisine' || variant === 'highlight' || variant === 'vibe') ? 'smd'
    : 'sm';
  const fontSize = sizeTier === 'mdl' ? '17px' : sizeTier === 'smd' ? '15px' : 'sm';
  const px = sizeTier === 'mdl' ? 5 : sizeTier === 'smd' ? 3.5 : 3;
  const py = sizeTier === 'mdl' ? 2.5 : sizeTier === 'smd' ? 2 : 1.5;
  const borderRadius = variant === 'toggle' ? '8px' : 'full';

  return {
    bg: 'transparent',
    color: textColor,
    border: 'none',
    fontSize,
    borderRadius,
    px,
    py,
    fontWeight,
    boxShadow: 'none',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    sx: gradientBorderSx(gradient),
  };
}

/**
 * Build the aria-label for variants that warrant one.
 */
function getAriaLabel(variant: UnifiedChipVariant, label: string): string | undefined {
  switch (variant) {
    case 'category':
      return `Restaurant category: ${label}`;
    case 'cuisine':
      return `Cuisine type: ${label}`;
    case 'vibe':
      return `Restaurant vibe: ${label}`;
    default:
      return undefined;
  }
}

// =============================================================================
// Component
// =============================================================================

/**
 * UnifiedChip renders a single styled chip/badge for restaurant attributes.
 *
 * @example
 * // Category chip
 * <UnifiedChip variant="category" label="Fine Dining" categoryKey="fine_dining" />
 *
 * @example
 * // Keyword chip with icon
 * <UnifiedChip variant="keyword" label="Wine Selection" icon={Wine} group="beverages" />
 *
 * @example
 * // Cuisine chip with flag
 * <UnifiedChip variant="cuisine" label="Italian" flagEmoji="\uD83C\uDDEE\uD83C\uDDF9" />
 *
 * @example
 * // Toggle chip with amount
 * <UnifiedChip variant="toggle" label="Happy Hour" icon={Clock} amount={2} amountUnit="hrs" />
 *
 * @example
 * // Vibe chip with emoji and custom gradient
 * <UnifiedChip variant="vibe" label="Al Fresco" flagEmoji="🌿" customGradient="linear-gradient(135deg, #ECFDF5, #ECFDF5)" />
 */
export const UnifiedChip = React.memo(function UnifiedChip({
  variant,
  label,
  icon: IconProp,
  iconColor,
  group,
  flagEmoji,
  accentColor,
  amount,
  amountUnit,
  categoryKey,
  learnMoreUrl,
  learnMoreToken,
  learnMorePending = false,
  customGradient,
}: UnifiedChipProps) {
  const prefersReducedMotion = useReducedMotion();
  const { sx, ...styles } = getVariantStyles(variant, customGradient);
  const hoverScale = HOVER_SCALES[variant];
  const iconSize = ICON_SIZES[variant];
  const ariaLabel = getAriaLabel(variant, label);
  const redeemLearnMoreView = useRedeemLearnMoreView();

  // Resolve icon: category variant uses categoryKey lookup, others use prop
  const ResolvedIcon: LucideIcon | undefined =
    variant === 'category' ? getCategoryIconComponent(categoryKey ?? '') : IconProp;

  const isInteractive = Boolean(learnMoreUrl) && !learnMorePending;

  const handleClick = useCallback(async () => {
    if (!learnMoreUrl || learnMorePending) return;
    analytics.capture(AnalyticsEvents.CUISINE_CHIP_LEARN_MORE_CLICKED, { cuisine_name: label });
    // Fire-and-forget: a redemption failure must never block opening the
    // URL the member is already looking at.
    if (learnMoreToken) {
      redeemLearnMoreView.mutate(learnMoreToken);
    }
    try {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: learnMoreUrl });
      } else {
        window.open(learnMoreUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to open learn more URL:', error);
    }
  }, [learnMoreUrl, learnMoreToken, learnMorePending, label, redeemLearnMoreView]);

  return (
    <MotionBadge
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      cursor={isInteractive ? 'pointer' : 'default'}
      onClick={isInteractive ? handleClick : undefined}
      textTransform="none"
      lineHeight="short"
      whiteSpace="nowrap"
      aria-label={ariaLabel}
      aria-disabled={learnMorePending ? true : undefined}
      whileHover={
        prefersReducedMotion || learnMorePending
          ? undefined
          : {
              scale: hoverScale,
              transition: { type: 'spring', stiffness: 300, damping: 20 },
            }
      }
      whileTap={
        prefersReducedMotion || learnMorePending
          ? undefined
          : {
              scale: 0.97,
              transition: { type: 'spring', stiffness: 300, damping: 20 },
            }
      }
      {...styles}
      sx={sx}
    >
      {/* Emoji prefix for cuisine flags and vibe icons */}
      {(variant === 'cuisine' || variant === 'vibe') && flagEmoji && (
        <Box as="span" fontSize="sm" lineHeight={1}>
          {flagEmoji}
        </Box>
      )}

      {/* Icon for non-cuisine variants */}
      {ResolvedIcon && variant !== 'cuisine' && (
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          flexShrink={0}
          color={iconColor ?? VARIANT_TEXT_COLORS[variant]}
        >
          <ResolvedIcon size={iconSize} />
        </Box>
      )}

      {/* Label text */}
      <Text as="span" lineHeight="short">
        {label}
      </Text>

      {/* Amount suffix for toggle variant */}
      {variant === 'toggle' && amount != null && (
        <Text as="span" fontWeight="semibold" lineHeight="short">
          {amount}
          {amountUnit ? ` ${amountUnit}` : ''}
        </Text>
      )}
    </MotionBadge>
  );
});

export default UnifiedChip;
